import { HttpClient } from "@angular/common/http";
import { inject, Injectable, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { UserProfile } from "@kno2/core/session/user-profile.model";
import { LocalStorageService } from "@kno2/core/storage";
import { BehaviorSubject, filter, firstValueFrom } from "rxjs";

@Injectable({
    providedIn: "root"
})
export class SessionService {
    private readonly localStorageKey = "ProfileData";
    private readonly localStorageService = inject(LocalStorageService);
    private readonly httpClient = inject(HttpClient);

    public profileData$ = new BehaviorSubject<UserProfile | null>(this.localStorageService.get(this.localStorageKey));
    public profileData = toSignal(this.profileData$.pipe(filter((profile) => !!profile && !!Object.entries(profile).length)));

    public readonly sessionExpired = signal(false);

    private readonly _profileConfirmed = signal(false);
    public readonly profileConfirmed = this._profileConfirmed.asReadonly();

    /**
     * Marks the profile as checked against this page load's access token. Until then the profile may be a previous session's copy from local storage.
     */
    public confirmProfile(): void {
        this._profileConfirmed.set(true);
    }

    /**
     * Gets the user profile from memory. If the profile is not in memory, it will be fetched from local storage, set in memory, and returned.
     *
     * @returns The user profile.
     */
    public getProfile(): UserProfile {
        let profile = this.profileData$.value;

        if (!profile) {
            profile = this.localStorageService.get(this.localStorageKey);
            this.profileData$.next(profile);
        }
        return profile;
    }

    /**
     * Refetches the user profile from the server and updates local storage.
     *
     * Use this when you need the profile from the server and to update memory and local storage.
     *
     * @returns The user profile.
     */
    public async refreshProfile(): Promise<UserProfile> {
        const profile = await firstValueFrom(this.httpClient.get<UserProfile>("/api/users/profiledata"));
        this.setProfile(profile);

        return profile;
    }

    /**
     * Gets the user profile from the server.
     *
     * @returns The user profile.
     *
     */
    public async getProfileAsync(): Promise<UserProfile> {
        let profile = this.getProfile();
        if (!profile || !Object.entries(profile).length) {
            profile = await this.refreshProfile();
        }

        return profile;
    }

    /**
     * Sets the user profile in memory and local storage.
     *
     * @param profile The new user profile.
     */
    public setProfile(profile: UserProfile): void {
        this.profileData$.next(profile);
        this.localStorageService.set(this.localStorageKey, profile);
    }

    /**
     * Removes the user profile from local storage.
     */
    public removeProfile(): void {
        this.localStorageService.remove(this.localStorageKey);
    }

    /**
     * Checks if the user is in one of the given roles.
     *
     * @param roles The roles to test.
     * @returns `true` if the user is in one of the roles. `false` otherwise.
     */
    public userInRole(roles: string | string[]): boolean {
        const profile = this.getProfile();
        const roleArray = Array.isArray(roles) ? roles : roles.split(",");
        return roleArray.some((role) => profile?.roles.includes(role));
    }

    /**
     * Checks if the user is in the SysAdmin or SysAdminReporting role.
     *
     * @returns `true` if the user is in the SysAdmin or SysAdminReporting role. `false` otherwise.
     */
    public isSysAdmin(): boolean {
        return this.userInRole(["SysAdmin", "SysAdminReporting"]);
    }

    /**
     * Gets the default route for the user's active role.
     *
     * @returns The default route for the user's active role.
     */
    public getDefaultRouteForActiveRole(): string {
        let path = "/intake";
        if (this.isSysAdmin()) {
            path = "/admin/reports";
        }
        if (this.userInRole("Administrator")) {
            path = "/account/dashboard";
        }
        return path;
    }

    /**
     * @description Gets the user's subdomain. E.g., starklabs
     *
     * @returns The user's subdomain
     *
     */
    public getSubDomain(): string {
        return this.getProfile().organizations.find((org) => org.id === this.getProfile().organizationId)!.subDomain;
    }

    /**
     * @description Gets the user's domain. E.g., starklabs.direct.kno2fy-dev.com
     *
     * @returns The user's domain, otherwise concatenated ${subDomain}.direct.${host}
     *
     */
    public getDomain(): string {
        const { domain, host } = this.getProfile();

        if (domain) return domain;
        const subDomain = this.getSubDomain();

        return `${subDomain}.direct.${host}`;
    }
}
